import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar, FiChevronLeft, FiChevronRight, FiClock, FiBriefcase, FiUsers, FiPlus, FiMapPin } from 'react-icons/fi';
import { getJobs, getAuthToken, getMeetings } from '../services/api';
import { API_URL } from '../config';
import axios from 'axios';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, parseISO, startOfWeek, endOfWeek } from 'date-fns';
import MeetingModal from '../components/MeetingModal';

const getAuthConfig = async () => {
  const token = await getAuthToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [jobs, setJobs] = useState([]);
  const [workedJobs, setWorkedJobs] = useState({}); // { 'YYYY-MM-DD': [{ job_id, job_number, client_name, total_hours }] }
  const [myDailyEntries, setMyDailyEntries] = useState({}); // { 'YYYY-MM-DD': { total_hours, clock_in, clock_out } }
  const [meetingsByDay, setMeetingsByDay] = useState({}); // { 'YYYY-MM-DD': [meeting, ...] }
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'week'
  const [editingMeeting, setEditingMeeting] = useState(null); // meeting object being edited
  const [newMeetingDate, setNewMeetingDate] = useState(null); // Date when adding new
  const [showMeetingModal, setShowMeetingModal] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const isCapacitor = window.Capacitor?.isNativePlatform?.() || false;
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isCapacitor || isSmallScreen);
      if (isCapacitor || isSmallScreen) {
        setViewMode('week');
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    loadJobs();
    loadWorkedJobs();
    loadMyDailyEntries();
    loadMeetings();
  }, [currentDate]);

  const loadMeetings = async () => {
    try {
      // Load a window wide enough for either the month or week view
      const rangeStart = startOfWeek(startOfMonth(currentDate));
      const rangeEnd = endOfWeek(endOfMonth(currentDate));
      const res = await getMeetings({
        start: rangeStart.toISOString(),
        end: rangeEnd.toISOString(),
      });
      const list = res.data.meetings || [];
      const grouped = {};
      list.forEach(m => {
        // start_time is UTC ISO; parse into local Date and bucket by local date key
        let s = m.start_time;
        if (s && !s.endsWith('Z') && !s.includes('+')) s = s + 'Z';
        const d = new Date(s);
        if (isNaN(d.getTime())) return;
        const key = format(d, 'yyyy-MM-dd');
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(m);
      });
      // Sort each day's meetings by start time
      Object.keys(grouped).forEach(k => {
        grouped[k].sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      });
      setMeetingsByDay(grouped);
    } catch (error) {
      console.error('Error loading meetings:', error);
    }
  };

  const ACTIVE_STATUSES = ['Pending', 'Scheduled', 'In Progress - Initial Field Work', 'In Progress - Topo',
    'In Progress - Additional Field Work Required', 'In Progress - Drafting', 'Needs Review',
    'Sent for Review', 'Final Mark Up / Stake Lines'];

  const loadJobs = async () => {
    try {
      // Narrow server-side: only scheduled jobs in an active status actually appear on
      // the calendar. Fetching every job and filtering here pulled ~2,300 rows to show ~50.
      const params = new URLSearchParams({
        scheduled: 'true',
        status_in: ACTIVE_STATUSES.join(','),
      });
      const response = await getJobs(params.toString());
      setJobs(response.data.jobs);
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  };

  const loadWorkedJobs = async () => {
    try {
      const config = await getAuthConfig();
      // Get time entries for the current month range
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      
      const response = await axios.get(
        `${API_URL}/time-entries?type=job&start_date=${format(monthStart, 'yyyy-MM-dd')}&end_date=${format(monthEnd, 'yyyy-MM-dd')}`,
        config
      );
      
      // Group time entries by date and job
      const grouped = {};
      const entries = response.data.time_entries || response.data || [];
      entries.forEach(entry => {
        if (!entry.job_id || !entry.clock_in) return;
        
        // Parse the clock_in date and convert to local date
        let clockInDate = new Date(entry.clock_in);
        // If no timezone indicator, treat as UTC
        if (!entry.clock_in.includes('Z') && !entry.clock_in.includes('+')) {
          clockInDate = new Date(entry.clock_in + 'Z');
        }
        const dateKey = format(clockInDate, 'yyyy-MM-dd');
        
        if (!grouped[dateKey]) {
          grouped[dateKey] = {};
        }
        
        if (!grouped[dateKey][entry.job_id]) {
          grouped[dateKey][entry.job_id] = {
            job_id: entry.job_id,
            job_number: entry.job_number || 'Unknown',
            client_name: entry.property_address || '',
            total_hours: 0,
            workers: new Set()
          };
        }
        
        grouped[dateKey][entry.job_id].total_hours += entry.total_hours || 0;
        if (entry.member_name) {
          grouped[dateKey][entry.job_id].workers.add(entry.member_name);
        }
      });
      
      // Convert to array format
      const result = {};
      Object.keys(grouped).forEach(date => {
        result[date] = Object.values(grouped[date]).map(job => ({
          ...job,
          workers: Array.from(job.workers)
        }));
      });
      
      setWorkedJobs(result);
      setLoading(false);
    } catch (error) {
      console.error('Error loading worked jobs:', error);
      setLoading(false);
    }
  };

  const loadMyDailyEntries = async () => {
    try {
      const config = await getAuthConfig();
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      
      const response = await axios.get(
        `${API_URL}/time-entries?type=daily&start_date=${format(monthStart, 'yyyy-MM-dd')}&end_date=${format(monthEnd, 'yyyy-MM-dd')}`,
        config
      );
      
      // Group daily entries by date (exclude time off)
      const grouped = {};
      const entries = response.data.time_entries || response.data || [];
      entries.forEach(entry => {
        if (!entry.clock_in) return;
        if (entry.work_type?.includes('Time Off')) return;
        
        // Parse the clock_in date
        let clockInDate = new Date(entry.clock_in);
        if (!entry.clock_in.includes('Z') && !entry.clock_in.includes('+')) {
          clockInDate = new Date(entry.clock_in + 'Z');
        }
        const dateKey = format(clockInDate, 'yyyy-MM-dd');
        
        if (!grouped[dateKey]) {
          grouped[dateKey] = {
            total_hours: 0,
            clock_in: entry.clock_in,
            clock_out: entry.clock_out
          };
        }
        
        grouped[dateKey].total_hours += entry.total_hours || 0;
        // Keep the earliest clock_in and latest clock_out
        if (entry.clock_in < grouped[dateKey].clock_in) {
          grouped[dateKey].clock_in = entry.clock_in;
        }
        if (entry.clock_out && (!grouped[dateKey].clock_out || entry.clock_out > grouped[dateKey].clock_out)) {
          grouped[dateKey].clock_out = entry.clock_out;
        }
      });
      
      setMyDailyEntries(grouped);
    } catch (error) {
      console.error('Error loading daily entries:', error);
    }
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // For week view
  const weekStart = startOfWeek(currentDate);
  const weekEnd = endOfWeek(currentDate);
  const daysInWeek = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const previousPeriod = () => {
    if (viewMode === 'week') {
      setCurrentDate(new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    }
  };

  const nextPeriod = () => {
    if (viewMode === 'week') {
      setCurrentDate(new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000));
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getJobsForDay = (day) => {
    return jobs.filter(job => isSameDay(parseISO(job.scheduled_date), day));
  };

  const getWorkedJobsForDay = (day) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    return workedJobs[dateKey] || [];
  };

  const getMyDailyForDay = (day) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    return myDailyEntries[dateKey] || null;
  };

  const getMeetingsForDay = (day) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    return meetingsByDay[dateKey] || [];
  };

  const formatMeetingTime = (iso) => {
    if (!iso) return '';
    let s = iso;
    if (!s.endsWith('Z') && !s.includes('+')) s = s + 'Z';
    const d = new Date(s);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const handleAddMeeting = (day) => {
    setEditingMeeting(null);
    setNewMeetingDate(day || new Date());
    setShowMeetingModal(true);
  };

  const handleEditMeeting = (meeting) => {
    setEditingMeeting(meeting);
    setNewMeetingDate(null);
    setShowMeetingModal(true);
  };

  const handleMeetingSaved = () => {
    setShowMeetingModal(false);
    setEditingMeeting(null);
    setNewMeetingDate(null);
    loadMeetings();
  };

  const renderDayContent = (day, isCompact = false) => {
    const scheduledJobs = getJobsForDay(day);
    const worked = getWorkedJobsForDay(day);
    const myDaily = getMyDailyForDay(day);
    const meetings = getMeetingsForDay(day);
    const isToday = isSameDay(day, new Date());
    const hasContent = scheduledJobs.length > 0 || worked.length > 0 || myDaily || meetings.length > 0;

    if (isCompact) {
      // Mobile compact view
      return (
        <div 
          key={day.toISOString()} 
          className={`calendar-day-mobile ${isToday ? 'today' : ''} ${hasContent ? 'has-content' : ''}`}
          onClick={() => setSelectedDay(day)}
          style={{
            padding: '8px',
            textAlign: 'center',
            borderRadius: '8px',
            backgroundColor: isToday ? '#2563eb' : (hasContent ? '#e0f2fe' : 'transparent'),
            color: isToday ? 'white' : '#333',
            cursor: 'pointer',
            position: 'relative'
          }}
        >
          <div style={{ fontWeight: isToday ? '600' : '400', fontSize: '14px' }}>
            {format(day, 'd')}
          </div>
          {hasContent && !isToday && (
            <div style={{
              position: 'absolute',
              bottom: '4px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '2px'
            }}>
              {scheduledJobs.length > 0 && <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#2563eb' }}></div>}
              {worked.length > 0 && <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#16a34a' }}></div>}
              {myDaily && <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#9333ea' }}></div>}
              {meetings.length > 0 && <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#f59e0b' }}></div>}
            </div>
          )}
        </div>
      );
    }

    // Helper to format time from ISO string
    const formatTimeOnly = (isoStr) => {
      if (!isoStr) return '';
      let date = new Date(isoStr);
      if (!isoStr.includes('Z') && !isoStr.includes('+')) {
        date = new Date(isoStr + 'Z');
      }
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    };

    // Desktop full view
    return (
      <div 
        key={day.toISOString()} 
        className={`calendar-day ${isToday ? 'today' : ''} ${hasContent ? 'has-jobs' : ''}`}
      >
        <div className="day-number">{format(day, 'd')}</div>
        <div className="day-jobs">
          {/* Meetings */}
          {meetings.map(meeting => (
            <div
              key={`meeting-${meeting.id}`}
              onClick={(e) => { e.stopPropagation(); handleEditMeeting(meeting); }}
              className="calendar-job meeting"
              title={meeting.title}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: '#fef3c7',
                color: '#92400e',
                fontSize: '11px',
                marginBottom: '2px',
                cursor: 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              <FiCalendar size={10} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {formatMeetingTime(meeting.start_time)} {meeting.title}
              </span>
            </div>
          ))}

          {/* My Daily Time Entry */}
          {myDaily && (
            <div 
              className="calendar-job daily"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: '#f3e8ff',
                color: '#7c3aed',
                fontSize: '11px',
                marginBottom: '2px'
              }}
            >
              <FiClock size={10} />
              <span>My Day: {myDaily.total_hours.toFixed(1)}h</span>
              {myDaily.clock_in && (
                <span style={{ fontSize: '9px', opacity: 0.8 }}>
                  ({formatTimeOnly(myDaily.clock_in)} - {myDaily.clock_out ? formatTimeOnly(myDaily.clock_out) : 'Active'})
                </span>
              )}
            </div>
          )}
          
          {/* Scheduled jobs */}
          {scheduledJobs.map(job => (
            <Link 
              key={`scheduled-${job.id}`} 
              to={`/jobs/${job.id}`}
              className="calendar-job scheduled"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: '#dbeafe',
                color: '#1e40af',
                fontSize: '11px',
                textDecoration: 'none',
                marginBottom: '2px'
              }}
            >
              <FiBriefcase size={10} />
              <span>{job.job_number}</span>
            </Link>
          ))}
          
          {/* Worked jobs */}
          {worked.map(job => (
            <Link 
              key={`worked-${job.job_id}`} 
              to={`/jobs/${job.job_id}`}
              className="calendar-job worked"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: '#dcfce7',
                color: '#166534',
                fontSize: '11px',
                textDecoration: 'none',
                marginBottom: '2px'
              }}
            >
              <FiClock size={10} />
              <span>{job.job_number} ({job.total_hours.toFixed(1)}h)</span>
            </Link>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        color: '#666'
      }}>
        Loading calendar...
      </div>
    );
  }

  // Mobile Layout
  if (isMobile) {
    const displayDays = viewMode === 'week' ? daysInWeek : daysInMonth;
    const selectedDayJobs = selectedDay ? getJobsForDay(selectedDay) : [];
    const selectedDayWorked = selectedDay ? getWorkedJobsForDay(selectedDay) : [];
    const selectedDayDaily = selectedDay ? getMyDailyForDay(selectedDay) : null;
    const selectedDayMeetings = selectedDay ? getMeetingsForDay(selectedDay) : [];

    // Helper to format time from ISO string
    const formatTimeOnly = (isoStr) => {
      if (!isoStr) return '';
      let date = new Date(isoStr);
      if (!isoStr.includes('Z') && !isoStr.includes('+')) {
        date = new Date(isoStr + 'Z');
      }
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    };

    return (
      <div style={{ padding: '15px', paddingBottom: '100px' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '15px'
        }}>
          <h1 style={{ fontSize: '20px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiCalendar /> Calendar
          </h1>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <button
              onClick={() => handleAddMeeting(selectedDay || new Date())}
              style={{
                padding: '6px 10px', borderRadius: '6px', border: 'none',
                backgroundColor: '#f59e0b', color: 'white', fontSize: '12px',
                display: 'inline-flex', alignItems: 'center', gap: '4px'
              }}
            >
              <FiPlus size={14} /> Meeting
            </button>
            <button
              onClick={() => setViewMode('week')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: viewMode === 'week' ? '#2563eb' : '#e5e7eb',
                color: viewMode === 'week' ? 'white' : '#333',
                fontSize: '12px'
              }}
            >
              Week
            </button>
            <button
              onClick={() => setViewMode('month')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: viewMode === 'month' ? '#2563eb' : '#e5e7eb',
                color: viewMode === 'month' ? 'white' : '#333',
                fontSize: '12px'
              }}
            >
              Month
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '15px',
          backgroundColor: '#f8fafc',
          padding: '10px',
          borderRadius: '10px'
        }}>
          <button 
            onClick={previousPeriod}
            style={{ 
              padding: '8px', 
              border: 'none', 
              background: 'none',
              cursor: 'pointer'
            }}
          >
            <FiChevronLeft size={24} />
          </button>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: '600', fontSize: '16px' }}>
              {viewMode === 'week' 
                ? `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`
                : format(currentDate, 'MMMM yyyy')
              }
            </div>
            <button 
              onClick={goToToday}
              style={{
                fontSize: '12px',
                color: '#2563eb',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              Today
            </button>
          </div>
          <button 
            onClick={nextPeriod}
            style={{ 
              padding: '8px', 
              border: 'none', 
              background: 'none',
              cursor: 'pointer'
            }}
          >
            <FiChevronRight size={24} />
          </button>
        </div>

        {/* Day Labels */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '4px',
          marginBottom: '8px'
        }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} style={{ 
              textAlign: 'center', 
              fontSize: '11px', 
              color: '#666',
              fontWeight: '500'
            }}>
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        {viewMode === 'week' ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px',
            marginBottom: '20px'
          }}>
            {daysInWeek.map(day => renderDayContent(day, true))}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px',
            marginBottom: '20px'
          }}>
            {/* Empty cells before month start */}
            {Array.from({ length: monthStart.getDay() }).map((_, index) => (
              <div key={`empty-${index}`} style={{ padding: '8px' }}></div>
            ))}
            {daysInMonth.map(day => renderDayContent(day, true))}
          </div>
        )}

        {/* Legend */}
        <div style={{
          display: 'flex',
          gap: '15px',
          justifyContent: 'center',
          marginBottom: '15px',
          fontSize: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#2563eb' }}></div>
            <span>Scheduled</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#16a34a' }}></div>
            <span>Worked</span>
          </div>
        </div>

        {/* Selected Day Details */}
        {selectedDay && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '15px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ 
              margin: '0 0 15px 0', 
              fontSize: '16px',
              borderBottom: '1px solid #eee',
              paddingBottom: '10px'
            }}>
              {format(selectedDay, 'EEEE, MMMM d, yyyy')}
            </h3>

            {selectedDayJobs.length === 0 && selectedDayWorked.length === 0 && !selectedDayDaily && selectedDayMeetings.length === 0 ? (
              <p style={{ color: '#666', margin: 0 }}>Nothing on this day.</p>
            ) : (
              <>
                {/* Meetings */}
                {selectedDayMeetings.length > 0 && (
                  <div style={{ marginBottom: '15px' }}>
                    <h4 style={{
                      margin: '0 0 10px 0', fontSize: '13px', color: '#92400e',
                      display: 'flex', alignItems: 'center', gap: '5px'
                    }}>
                      <FiCalendar size={14} /> Meetings
                    </h4>
                    {selectedDayMeetings.map(m => (
                      <div
                        key={m.id}
                        onClick={() => handleEditMeeting(m)}
                        style={{
                          display: 'block', padding: '10px',
                          backgroundColor: '#fef3c7', color: '#92400e',
                          borderRadius: '8px', marginBottom: '8px', cursor: 'pointer'
                        }}
                      >
                        <div style={{ fontWeight: '600' }}>{m.title}</div>
                        <div style={{ fontSize: '12px', marginTop: '4px' }}>
                          ⏰ {formatMeetingTime(m.start_time)}
                          {m.end_time ? ` – ${formatMeetingTime(m.end_time)}` : ''}
                        </div>
                        {m.location && (
                          <div style={{ fontSize: '12px', marginTop: '4px' }}>
                            📍 {m.location}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* My Daily Time */}
                {selectedDayDaily && (
                  <div style={{ marginBottom: '15px' }}>
                    <h4 style={{ 
                      margin: '0 0 10px 0', 
                      fontSize: '13px', 
                      color: '#7c3aed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <FiClock size={14} /> My Daily Time
                    </h4>
                    <div 
                      style={{
                        display: 'block',
                        padding: '10px',
                        backgroundColor: '#f3e8ff',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        color: '#7c3aed'
                      }}
                    >
                      <div style={{ fontWeight: '600' }}>{selectedDayDaily.total_hours.toFixed(1)} hours</div>
                      {selectedDayDaily.clock_in && (
                        <div style={{ fontSize: '12px', marginTop: '4px' }}>
                          ⏰ {formatTimeOnly(selectedDayDaily.clock_in)} - {selectedDayDaily.clock_out ? formatTimeOnly(selectedDayDaily.clock_out) : 'Active'}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Scheduled Jobs */}
                {selectedDayJobs.length > 0 && (
                  <div style={{ marginBottom: '15px' }}>
                    <h4 style={{ 
                      margin: '0 0 10px 0', 
                      fontSize: '13px', 
                      color: '#1e40af',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <FiBriefcase size={14} /> Scheduled
                    </h4>
                    {selectedDayJobs.map(job => (
                      <Link 
                        key={job.id} 
                        to={`/jobs/${job.id}`}
                        style={{
                          display: 'block',
                          padding: '10px',
                          backgroundColor: '#dbeafe',
                          borderRadius: '8px',
                          marginBottom: '8px',
                          textDecoration: 'none',
                          color: '#1e40af'
                        }}
                      >
                        <div style={{ fontWeight: '600' }}>{job.job_number}</div>
                        <div style={{ fontSize: '13px' }}>{job.client_name}</div>
                        {job.scheduled_time && (
                          <div style={{ fontSize: '12px', marginTop: '4px' }}>
                            ⏰ {job.scheduled_time}
                          </div>
                        )}
                        {job.team_members && job.team_members.length > 0 && (
                          <div style={{ fontSize: '12px', color: '#3b82f6', marginTop: '4px' }}>
                            <FiUsers size={11} /> {job.team_members.map(m => m.name || m).join(', ')}
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                )}

                {/* Worked Jobs */}
                {selectedDayWorked.length > 0 && (
                  <div>
                    <h4 style={{ 
                      margin: '0 0 10px 0', 
                      fontSize: '13px', 
                      color: '#166534',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <FiClock size={14} /> Work Completed
                    </h4>
                    {selectedDayWorked.map(job => (
                      <Link 
                        key={job.job_id} 
                        to={`/jobs/${job.job_id}`}
                        style={{
                          display: 'block',
                          padding: '10px',
                          backgroundColor: '#dcfce7',
                          borderRadius: '8px',
                          marginBottom: '8px',
                          textDecoration: 'none',
                          color: '#166534'
                        }}
                      >
                        <div style={{ fontWeight: '600' }}>{job.job_number}</div>
                        <div style={{ fontSize: '13px' }}>{job.client_name}</div>
                        <div style={{ fontSize: '12px', marginTop: '4px' }}>
                          ⏱ {job.total_hours.toFixed(1)} hours
                          {job.workers.length > 0 && ` • ${job.workers.join(', ')}`}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {showMeetingModal && (
          <MeetingModal
            meeting={editingMeeting}
            defaultDate={newMeetingDate}
            onClose={() => { setShowMeetingModal(false); setEditingMeeting(null); setNewMeetingDate(null); }}
            onSaved={handleMeetingSaved}
          />
        )}
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="calendar-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1><FiCalendar /> Job Calendar</h1>
          <p className="subtitle">View scheduled and worked jobs</p>
        </div>
        <button
          onClick={() => handleAddMeeting(new Date())}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '8px 14px', backgroundColor: '#f59e0b', color: 'white',
            border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          <FiPlus size={16} /> Add Meeting
        </button>
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '15px',
        fontSize: '13px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ 
            padding: '2px 8px', 
            backgroundColor: '#dbeafe', 
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <FiBriefcase size={12} />
            Scheduled
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{
            padding: '2px 8px',
            backgroundColor: '#dcfce7',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <FiClock size={12} />
            Worked
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{
            padding: '2px 8px',
            backgroundColor: '#fef3c7',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <FiCalendar size={12} />
            Meeting
          </div>
        </div>
      </div>

      <div className="calendar-controls">
        <button className="btn btn-ghost" onClick={previousPeriod}>
          <FiChevronLeft /> Previous
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <h2 style={{ margin: 0 }}>{format(currentDate, 'MMMM yyyy')}</h2>
          <button 
            className="btn btn-ghost" 
            onClick={goToToday}
            style={{ fontSize: '13px' }}
          >
            Today
          </button>
        </div>
        <button className="btn btn-ghost" onClick={nextPeriod}>
          Next <FiChevronRight />
        </button>
      </div>

      <div className="calendar-grid">
        <div className="calendar-header">
          <div className="calendar-day-label">Sun</div>
          <div className="calendar-day-label">Mon</div>
          <div className="calendar-day-label">Tue</div>
          <div className="calendar-day-label">Wed</div>
          <div className="calendar-day-label">Thu</div>
          <div className="calendar-day-label">Fri</div>
          <div className="calendar-day-label">Sat</div>
        </div>

        <div className="calendar-days">
          {/* Add empty cells for days before month starts */}
          {Array.from({ length: monthStart.getDay() }).map((_, index) => (
            <div key={`empty-${index}`} className="calendar-day empty"></div>
          ))}

          {/* Render days of the month */}
          {daysInMonth.map(day => renderDayContent(day, false))}
        </div>
      </div>

      {/* Jobs Legend */}
      <div className="calendar-legend">
        <h3>Activity This Month</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Scheduled Jobs */}
          <div>
            <h4 style={{ 
              color: '#1e40af', 
              fontSize: '14px', 
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <FiBriefcase /> Scheduled Jobs
            </h4>
            {jobs.filter(job => {
              const jobDate = parseISO(job.scheduled_date);
              return isSameMonth(jobDate, currentDate);
            }).length > 0 ? (
              <div className="legend-list">
                {jobs
                  .filter(job => {
                    const jobDate = parseISO(job.scheduled_date);
                    return isSameMonth(jobDate, currentDate);
                  })
                  .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))
                  .map(job => (
                    <Link key={job.id} to={`/jobs/${job.id}`} className="legend-item">
                      <span className="legend-date">
                        {format(parseISO(job.scheduled_date), 'MMM d')}
                      </span>
                      <span className="legend-job">
                        <strong>{job.job_number}</strong> - {job.client_name}
                        {job.scheduled_time && ` at ${job.scheduled_time}`}
                      </span>
                    </Link>
                  ))
                }
              </div>
            ) : (
              <p style={{ color: '#666', fontSize: '13px' }}>No jobs scheduled this month</p>
            )}
          </div>

          {/* Worked Jobs */}
          <div>
            <h4 style={{ 
              color: '#166534', 
              fontSize: '14px', 
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <FiClock /> Work Completed
            </h4>
            {Object.keys(workedJobs).filter(dateKey => {
              const jobDate = parseISO(dateKey);
              return isSameMonth(jobDate, currentDate);
            }).length > 0 ? (
              <div className="legend-list">
                {Object.entries(workedJobs)
                  .filter(([dateKey]) => {
                    const jobDate = parseISO(dateKey);
                    return isSameMonth(jobDate, currentDate);
                  })
                  .sort(([a], [b]) => new Date(a) - new Date(b))
                  .map(([dateKey, dayJobs]) => (
                    dayJobs.map(job => (
                      <Link key={`${dateKey}-${job.job_id}`} to={`/jobs/${job.job_id}`} className="legend-item" style={{ color: '#166534' }}>
                        <span className="legend-date">
                          {format(parseISO(dateKey), 'MMM d')}
                        </span>
                        <span className="legend-job">
                          <strong>{job.job_number}</strong> - {job.total_hours.toFixed(1)}h
                        </span>
                      </Link>
                    ))
                  ))
                }
              </div>
            ) : (
              <p style={{ color: '#666', fontSize: '13px' }}>No work logged this month</p>
            )}
          </div>
        </div>
      </div>

      {showMeetingModal && (
        <MeetingModal
          meeting={editingMeeting}
          defaultDate={newMeetingDate}
          onClose={() => { setShowMeetingModal(false); setEditingMeeting(null); setNewMeetingDate(null); }}
          onSaved={handleMeetingSaved}
        />
      )}
    </div>
  );
}

export default Calendar;